import { useState, useEffect, useCallback } from 'react';
import type { FC } from 'react';
import { api, type RoomInviteResponse } from '../lib/api.js';

export const BOT_IDENTITY = 'jabberr-bot';
export const BOT_NAME = 'Jabberr';

export type User = {
  id: string;
  name: string;
  email: string;
  status: 'online' | 'offline' | 'unavailable';
  inRoom: boolean;
  hasActiveSession?: boolean;
};

export type MemberPresence = { status: 'online' | 'offline' | 'unavailable'; hasActiveSession?: boolean };
export type MemberStatus = 'in-meeting' | 'available' | 'unavailable' | 'offline';

/** Pure helper: a project member's status in the people panel. */
export function memberStatus(member: { id: string; name: string }, inRoomIds: string[], presence: MemberPresence): MemberStatus {
  // By user id only: two members can share a name (there are two John Sarvers), an id is one person.
  if (inRoomIds.includes(member.id)) return 'in-meeting';
  if (presence.status === 'unavailable') return 'unavailable';
  if (presence.status === 'online' && presence.hasActiveSession) return 'available';
  return 'offline';
}

export const MEMBER_STATUS_LABEL: Record<MemberStatus, string> = {
  'in-meeting': 'In this meeting',
  available: 'Available',
  unavailable: 'Unavailable',
  offline: 'Offline',
};

export type RoomPanelProps = {
  me: User;
  users: User[];
  inRoomIds: string[];
  onInvite: (user: User) => void;
  onStatusChange: (status: 'available' | 'unavailable') => void;
  state: 'idle' | 'connecting' | 'connected' | 'unavailable';
  meetingId: string;
  jabberrInRoom?: boolean;
};

const STATUS_OPTIONS = ['available', 'unavailable'] as const;

export const RoomPanel: FC<RoomPanelProps> = ({ me, users, inRoomIds, onInvite, onStatusChange, state, meetingId, jabberrInRoom }) => {
  const [selfStatus, setSelfStatus] = useState<'available' | 'unavailable'>('available');
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());
  const [botState, setBotState] = useState<'idle' | 'inviting' | 'in-room'>('idle');
  const isBotInRoom = botState === 'in-room';
  const isBotInviting = botState === 'inviting';
  const [botError, setBotError] = useState<string | null>(null);
  const [botListenMode, setBotListenMode] = useState<RoomInviteResponse['listenMode'] | null>(null);

  const botDataInRoom = users.some(u => u.id === BOT_IDENTITY || u.name === BOT_IDENTITY);

  useEffect(() => {
    setSelfStatus(me.status === 'online' ? 'available' : 'unavailable');
  }, [me.status]);

  useEffect(() => {
    const bot = users.find(u => u.id === BOT_IDENTITY);
    if (bot?.inRoom) {
      setBotState('in-room');
      setBotError(null);
    }
  }, [users]);

  const inviteBot = useCallback(async () => {
    setBotState('inviting');
    setBotError(null);
    try {
      await api.meetings.inviteJabberr(meetingId);
      setBotState('in-room');
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string };
      if (e.status === 503) {
        setBotError('Jabberr cannot join right now.');
      }
      setBotState('idle');
    }
  }, [meetingId]);

  const removeBot = useCallback(async () => {
    setBotState('inviting');
    setBotError(null);
    try {
      await api.meetings.removeJabberr(meetingId);
      setBotState('idle');
    } catch {
      setBotState('idle');
    }
  }, [meetingId]);

  const handleSelfClick = () => {
    const next = selfStatus === 'available' ? 'unavailable' : 'available';
    setSelfStatus(next);
    onStatusChange(next);
  };

  const handleInvite = (user: User) => {
    setInvitedIds(prev => new Set(prev).add(user.id));
    onInvite(user);
    window.setTimeout(() => {
      setInvitedIds(prev => {
        const next = new Set(prev);
        next.delete(user.id);
        return next;
      });
    }, 30_000);
  };

  if (state !== 'connected') return null;

  // Use the passed jabberrInRoom (from the status bar's source) to decide the Jabberr row.
  // Fall back to botState and data for backward compatibility.
  const effectiveBotInRoom = jabberrInRoom || botState === 'in-room' || botDataInRoom;

  const modeHints: Record<string, string> = {
    always: 'Jabberr answers everything said.',
    mentions: 'Jabberr answers when someone says his name.',
    keen: 'Jabberr speaks when he has something worth adding.',
  };

  return (
    <div className="room-invite-container">
      <div className="room-invite-list">
        <h3>People</h3>
        <div className="room-invite-user room-bot-row">
          <img
            src="/apple-touch-icon.png"
            alt=""
            className="room-bot-avatar"
            width="32"
            height="32"
          />
          <span className="room-invite-name">{BOT_NAME}</span>
          {effectiveBotInRoom ? (
            <span className="room-invite-status room-chip">In room</span>
          ) : (
            <button
              type="button"
              className="room-invite-btn btn-soft"
              onClick={() => void inviteBot()}
              disabled={isBotInviting}
              aria-label="Invite Jabberr"
            >
              Invite
            </button>
          )}
          {effectiveBotInRoom && (
            <button
              type="button"
              className="room-ghost-btn"
              onClick={() => void removeBot()}
              disabled={isBotInviting}
              aria-label="Remove Jabberr"
              title="Remove"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          )}
        </div>
        {botError && <span className="room-bot-error">{botError}</span>}
        {[...users].sort((a, b) => Number(inRoomIds.includes(b.id)) - Number(inRoomIds.includes(a.id))).map(user => {
          if (user.id === BOT_IDENTITY || user.name === BOT_IDENTITY) return null;
          const status = memberStatus(user, inRoomIds, user);
          const invited = invitedIds.has(user.id);
          return (
            <div key={user.id} className="room-invite-user">
              <span
                className={`room-dot-${status === 'in-meeting' ? 'online' : status === 'unavailable' ? 'unavailable' : 'offline'}`}
                role="img"
                aria-label={MEMBER_STATUS_LABEL[status]}
              ></span>
              <span className="room-invite-name">
                {user.name}
                {user.id === me.id ? ' (you)' : ''}
              </span>
              <span className={`room-invite-status ${status === 'available' ? 'room-invite-status-ok' : ''}`}>{MEMBER_STATUS_LABEL[status]}</span>
              {status !== 'in-meeting' ? (
                invited ? (
                  <span className="room-invite-status">Invited</span>
                ) : (
                  <button
                    onClick={() => handleInvite(user)}
                    className="room-invite-btn"
                    aria-label={`Invite ${user.name}`}
                  >
                    Invite
                  </button>
                )
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="room-self-status" onClick={handleSelfClick}>
        <span
          className={`room-dot-${selfStatus === 'available' ? 'online' : 'unavailable'}`}
          role="img"
          aria-label={
            selfStatus === 'available'
              ? 'Available'
              : 'Unavailable'
          }
        ></span>
        <div className="segment-control" role="radiogroup" aria-label="Your status">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt}
              type="button"
              className={`segment-control-btn ${opt === selfStatus ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                const next = opt === selfStatus ? (opt === 'available' ? 'unavailable' : 'available') : opt;
                setSelfStatus(next);
                onStatusChange(next);
              }}
              role="radio"
              aria-checked={opt === selfStatus}
              aria-label={`${opt === 'available' ? 'Available' : 'Unavailable'}`}
            >
              {opt === 'available' ? 'Available' : 'Unavailable'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
